'use client';

import { useMemo, useState } from 'react';

import { StatusVariant } from '@web/types';

import { MainButton, SecondaryButton } from '../atomic-atoms';

import { Modal } from './Modal';
import { SmartInput } from './SmartInput/SmartInput';

type ConfirmActionModalProps = {
  open: boolean;
  title: string;
  description: string;
  confirmText?: string;
  requireConfirmationText?: boolean;
  confirmButtonLabel?: string;
  confirmButtonVariant?: 'primary' | StatusVariant;
  cancelButtonLabel?: string;
  isProcessing?: boolean;
  onConfirm: () => Promise<void> | void;
  onClose: () => void;
};

export function ConfirmActionModal({
  open,
  title,
  description,
  confirmText = 'DELETE',
  requireConfirmationText = true,
  confirmButtonLabel = 'Confirm',
  confirmButtonVariant = 'error',
  cancelButtonLabel = 'Cancel',
  isProcessing = false,
  onConfirm,
  onClose,
}: ConfirmActionModalProps) {
  const [inputValue, setInputValue] = useState<string>('');

  const canConfirm = useMemo(
    () =>
      !requireConfirmationText ||
      inputValue.trim().toUpperCase() === confirmText.trim().toUpperCase(),
    [inputValue, confirmText, requireConfirmationText],
  );

  return (
    <Modal open={open} onClose={onClose} width="min(520px, 92vw)">
      <div className="card-container-glass">
        <h3
          style={{ margin: '0 0 0.5rem', fontSize: '1.3rem', fontWeight: 600 }}
        >
          {title}
        </h3>
        <p style={{ margin: '0 0 1.5rem', lineHeight: 1.5 }}>{description}</p>

        {requireConfirmationText ? (
          <SmartInput
            name="confirm-text"
            type="text"
            label={`Type "${confirmText?.toUpperCase() ?? ''}" to confirm`}
            value={inputValue}
            onChange={(value) => setInputValue(value.toUpperCase())}
          />
        ) : null}

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.75rem',
            marginTop: '1.25rem',
          }}
        >
          <SecondaryButton ariaLabel={cancelButtonLabel} onClick={onClose}>
            {cancelButtonLabel}
          </SecondaryButton>

          <MainButton
            ariaLabel={confirmButtonLabel}
            variant={confirmButtonVariant}
            onClick={async () => {
              if (!canConfirm || isProcessing) return;
              await onConfirm();
              setInputValue('');
            }}
            disabled={!canConfirm || isProcessing}
            isLoading={isProcessing}
          >
            {confirmButtonLabel}
          </MainButton>
        </div>
      </div>
    </Modal>
  );
}
